import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Camera, Check, Clock, FloppyDisk, PencilSimple, Plus, Storefront, Trash } from "@phosphor-icons/react";
import { API, getToken, clearToken, authHeaders } from "@/lib/api";
import { formatPrice } from "@/components/booking/format";
import TeamTab from "@/components/panel/TeamTab";

const CATEGORIES_BR = ["Cortes", "Barba", "Sobrancelha", "Química", "Combo", "Outros"];
const CATEGORIES_PY = ["Cortes", "Barba", "Cejas", "Química", "Combo", "Otros"];
const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export default function SuperAdminBarberEdit({ backTo = "/admin" }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [barber, setBarber] = useState(null);
  const [form, setForm] = useState(null);
  const [schedule, setSchedule] = useState({});
  const [banner, setBanner] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [svcForm, setSvcForm] = useState({ name: "", category: "Cortes", duration_min: "30", price: "" });
  const [editingSvc, setEditingSvc] = useState(null);
  const [savingSvc, setSavingSvc] = useState(false);
  const serviceFormRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/superadmin/barbers/${id}`, { headers: authHeaders() });
      const b = res.data;
      setBarber(b);
      setForm({ shop_name: b.shop_name || "", description: b.description || "", whatsapp: b.whatsapp || "", address: b.address || "", city: b.city || "" });
      setSchedule({ ...(b.schedule || {}) });
      setBanner(b.banner || 1);
    } catch (e) {
      if (e.response?.status === 401) { clearToken(); return navigate("/entrar"); }
      toast.error("Erro ao carregar barbearia");
      navigate(backTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate, backTo]);

  useEffect(() => {
    if (!getToken()) return navigate("/entrar");
    load();
  }, [load, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleDay = (wd) => setSchedule((s) => {
    const next = { ...s };
    if (next[String(wd)]) delete next[String(wd)];
    else next[String(wd)] = { open: "09:00", close: "19:00" };
    return next;
  });
  const setDayTime = (wd, field, value) => setSchedule((s) => ({ ...s, [String(wd)]: { ...s[String(wd)], [field]: value } }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/barbers/${id}/profile`, { ...form, schedule, banner }, { headers: authHeaders() });
      toast.success("Página do barbeiro atualizada");
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/superadmin/barbers/${id}/photo`, fd, { headers: authHeaders() });
      toast.success("Foto atualizada");
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao enviar foto");
    } finally {
      setUploading(false);
    }
  };

  const saveService = async (e) => {
    e.preventDefault();
    if (svcForm.name.trim().length < 2) return toast.warning("Informe o nome do serviço");
    const payload = {
      name: svcForm.name.trim(),
      category: svcForm.category,
      duration_min: Number(svcForm.duration_min) || 30,
      price: svcForm.price === "" ? null : Number(svcForm.price.replace(",", ".")),
    };
    setSavingSvc(true);
    try {
      if (editingSvc) {
        await axios.put(`${API}/superadmin/barbers/${id}/services/${editingSvc}`, payload, { headers: authHeaders() });
        toast.success("Serviço atualizado");
      } else {
        await axios.post(`${API}/superadmin/barbers/${id}/services`, payload, { headers: authHeaders() });
        toast.success("Serviço adicionado");
      }
      setSvcForm({ name: "", category: "Cortes", duration_min: "30", price: "" });
      setEditingSvc(null);
      load();
    } catch {
      toast.error("Erro ao salvar serviço");
    } finally {
      setSavingSvc(false);
    }
  };

  const removeService = async (sid) => {
    try {
      await axios.delete(`${API}/superadmin/barbers/${id}/services/${sid}`, { headers: authHeaders() });
      toast.success("Serviço removido");
      load();
    } catch {
      toast.error("Erro ao remover serviço");
    }
  };

  const inputCls = "mt-1.5 w-full h-11 px-3 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors text-sm";
  const isPy = barber?.country === "py";
  const CATEGORIES = isPy ? CATEGORIES_PY : CATEGORIES_BR;
  const priceLabel = (p) => (p == null ? (isPy ? "A convenir" : "A combinar") : formatPrice(p, isPy ? "es" : "pt"));

  if (!barber || !form) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <div className="w-full max-w-md mx-auto px-4 py-6 pb-16" data-testid="admin-barber-edit-page">
        <div className="flex items-center justify-between">
          <button data-testid="back-to-admin-btn" onClick={() => navigate(backTo)} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-amber-500 transition-colors">
            <ArrowLeft size={16} weight="bold" /> Voltar
          </button>
          <a href={`/b/${barber.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-500 hover:underline" data-testid="view-public-page-link">
            Ver página pública
          </a>
        </div>

        <div className="flex items-center gap-3 mt-5">
          <Storefront size={22} weight="duotone" className="text-amber-500 shrink-0" />
          <div className="min-w-0">
            <h1 className="font-display font-bold text-xl truncate" data-testid="edit-barber-name">{barber.shop_name}</h1>
            <p className="text-xs text-zinc-500">/b/{barber.slug} · {barber.country === "py" ? "Paraguay" : "Brasil"}</p>
          </div>
        </div>

        <form onSubmit={saveProfile} className="mt-6 space-y-6">
          <div className="flex items-center gap-4">
            {barber.photo_url ? (
              <img src={barber.photo_url} alt="Foto" className="w-20 h-20 rounded-full object-cover border-2 border-amber-500" data-testid="admin-photo-preview" />
            ) : (
              <div className="w-20 h-20 rounded-full border-2 border-zinc-700 bg-zinc-800 flex items-center justify-center" data-testid="admin-photo-empty">
                <Camera size={24} className="text-zinc-500" weight="duotone" />
              </div>
            )}
            <label className="cursor-pointer">
              <input data-testid="admin-photo-input" type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
              <span className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-zinc-800 text-sm font-semibold text-zinc-300 hover:border-amber-500/60 hover:text-amber-500 transition-colors">
                <Camera size={16} weight="duotone" />
                {uploading ? "Enviando..." : "Trocar foto"}
              </span>
            </label>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-zinc-300">Nome da barbearia</label>
              <input data-testid="admin-shop-input" type="text" value={form.shop_name} onChange={set("shop_name")} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-semibold text-zinc-300">Descrição</label>
              <textarea data-testid="admin-description-input" value={form.description} onChange={set("description")} rows={3} className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none transition-colors resize-none text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-zinc-300">WhatsApp</label>
                <input data-testid="admin-whatsapp-input" type="tel" value={form.whatsapp} onChange={set("whatsapp")} className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-semibold text-zinc-300">Cidade</label>
                <input data-testid="admin-city-input" type="text" value={form.city} onChange={set("city")} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-zinc-300">Endereço</label>
              <input data-testid="admin-address-input" type="text" value={form.address} onChange={set("address")} className={inputCls} />
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-zinc-300 mb-3">Banner da página</h3>
            <div className="grid grid-cols-3 gap-2" data-testid="admin-banner-picker">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`admin-banner-option-${n}`}
                  onClick={() => setBanner(n)}
                  className={`relative rounded-lg overflow-hidden border-2 transition-colors duration-200 ${banner === n ? "border-amber-500" : "border-zinc-800 hover:border-zinc-600"}`}
                >
                  <img src={`/banner-${n}.webp`} alt={`Banner ${n}`} className="w-full h-14 object-cover" />
                  {banner === n && (
                    <span className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                      <Check size={20} weight="bold" className="text-amber-400 drop-shadow" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-zinc-300 mb-3">Horários de funcionamento</h3>
            <div className="space-y-2" data-testid="admin-schedule-editor">
              {WEEKDAYS.map((label, wd) => {
                const cfg = schedule[String(wd)];
                return (
                  <div key={wd} className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5" data-testid={`admin-schedule-row-${wd}`}>
                    <label className="flex items-center gap-2 w-20 shrink-0 cursor-pointer">
                      <input data-testid={`admin-schedule-toggle-${wd}`} type="checkbox" checked={!!cfg} onChange={() => toggleDay(wd)} className="h-4 w-4 accent-amber-500" />
                      <span className="text-sm text-zinc-300">{label}</span>
                    </label>
                    {cfg ? (
                      <div className="flex items-center gap-2 flex-1">
                        <input data-testid={`admin-schedule-open-${wd}`} type="time" value={cfg.open} onChange={(e) => setDayTime(wd, "open", e.target.value)} className="flex-1 h-9 px-2 rounded-md bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm focus:border-amber-500 focus:outline-none" />
                        <span className="text-zinc-600 text-xs">às</span>
                        <input data-testid={`admin-schedule-close-${wd}`} type="time" value={cfg.close} onChange={(e) => setDayTime(wd, "close", e.target.value)} className="flex-1 h-9 px-2 rounded-md bg-zinc-950 border border-zinc-800 text-zinc-100 text-sm focus:border-amber-500 focus:outline-none" />
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-600">Fechado</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <button data-testid="admin-profile-save-btn" type="submit" disabled={saving} className="w-full h-12 rounded-lg bg-amber-500 text-zinc-950 font-bold flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60">
            <FloppyDisk size={18} weight="bold" />
            {saving ? "Salvando..." : "Salvar página"}
          </button>
        </form>

        <div className="mt-10">
          <h3 className="text-sm font-bold text-zinc-300 mb-3">Serviços ({barber.services?.length || 0})</h3>
          <form onSubmit={saveService} ref={serviceFormRef} className={`bg-zinc-900 border rounded-xl p-4 space-y-3 transition-colors ${editingSvc ? "border-amber-500/60" : "border-zinc-800"}`} data-testid="admin-service-form">
            {editingSvc && (
              <p className="text-xs font-bold text-amber-500" data-testid="admin-service-editing-label">Editando serviço — altere e clique em Salvar</p>
            )}
            <input data-testid="admin-service-name-input" type="text" value={svcForm.name} onChange={(e) => setSvcForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nome (ex: Corte Degrade)" className={inputCls.replace("mt-1.5 ", "")} />
            <div className="flex gap-3">
              <select data-testid="admin-service-category-select" value={svcForm.category} onChange={(e) => setSvcForm((f) => ({ ...f, category: e.target.value }))} className={`${inputCls.replace("mt-1.5 ", "")} flex-1`}>
                {[...new Set([...CATEGORIES, svcForm.category])].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="admin-service-duration-input" type="number" min="5" step="5" value={svcForm.duration_min} onChange={(e) => setSvcForm((f) => ({ ...f, duration_min: e.target.value }))} placeholder="Min" className={`${inputCls.replace("mt-1.5 ", "")} w-24`} />
            </div>
            <input data-testid="admin-service-price-input" type="text" inputMode="decimal" value={svcForm.price} onChange={(e) => setSvcForm((f) => ({ ...f, price: e.target.value }))} placeholder="Preço (vazio = a combinar)" className={inputCls.replace("mt-1.5 ", "")} />
            <div className="flex gap-2">
              <button data-testid="admin-service-save-btn" type="submit" disabled={savingSvc} className="flex-1 h-11 rounded-lg bg-amber-500 text-zinc-950 font-bold flex items-center justify-center gap-2 hover:bg-amber-400 transition-colors active:scale-95 disabled:opacity-60">
                <Plus size={16} weight="bold" />
                {savingSvc ? "Salvando..." : editingSvc ? "Salvar" : "Adicionar"}
              </button>
              {editingSvc && (
                <button data-testid="admin-service-cancel-btn" type="button" onClick={() => { setEditingSvc(null); setSvcForm({ name: "", category: "Cortes", duration_min: "30", price: "" }); }} className="h-11 px-4 rounded-lg border border-zinc-800 text-zinc-400 text-sm font-semibold hover:border-zinc-600 transition-colors">
                  Cancelar
                </button>
              )}
            </div>
          </form>

          <div className="mt-4 space-y-2" data-testid="admin-services-list">
            {(barber.services || []).length === 0 ? (
              <p className="text-sm text-zinc-500 bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="admin-no-services-msg">Nenhum serviço cadastrado.</p>
            ) : (
              barber.services.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3" data-testid={`admin-service-row-${s.id}`}>
                  <div className="min-w-0">
                    <p className="font-semibold text-zinc-100 truncate">{s.name}</p>
                    <p className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                      <span className="text-amber-500/80">{s.category}</span> · <Clock size={12} weight="duotone" /> {s.duration_min} min · {priceLabel(s.price)}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button data-testid={`admin-service-edit-btn-${s.id}`} onClick={() => { setEditingSvc(s.id); setSvcForm({ name: s.name, category: s.category, duration_min: String(s.duration_min), price: s.price == null ? "" : String(s.price) }); serviceFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }} className="p-2 text-zinc-400 hover:text-amber-500 transition-colors">
                      <PencilSimple size={17} weight="bold" />
                    </button>
                    <button data-testid={`admin-service-delete-btn-${s.id}`} onClick={() => removeService(s.id)} className="p-2 text-zinc-500 hover:text-red-400 transition-colors">
                      <Trash size={17} weight="bold" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-zinc-800">
          <TeamTab apiBase={`${API}/superadmin/barbers/${id}/professionals`} />
        </div>
      </div>
    </div>
  );
}
